import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { GridHeader } from "../../../components/Grid/Grid";
import { useSelector } from "react-redux";
import { permissionsInterface } from "../../onBoarding/onBoardingTypes";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { Pencil, Plus, Trash } from "react-bootstrap-icons";
import routePaths from "../../../Navigation/routePaths";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";
import WebService from "../../../utility/WebService";
import { toast } from "react-toastify";
import ManageFloors from "./ManageFloors";
import AddUpdateTower from "./AddUpdateTower";
import ExpandableGrid, { ExpandableRow } from "../../../components/Grid/ExpandableGrid";
import MarkEntity from "./MarkEntity";
import { FaMarker } from "react-icons/fa";

export type Coordinate = [number, number];

export interface ICoordinates {
    id: string;
    tower_id: string;
    coordinates: Coordinate[];
    tags: string[];
    unit_id?: string;
    floor_id?: string;
    type: "TOWER" | "FLOOR" | "UNIT";
}

export interface ITowerInfo {
    id: string;
    tower_name: string;
    floor_info: IFloorInfo[];
    image: string;
}

export interface IFloorInfo {
    id: string;
    floor_name: string;
    units: string[];
    image: string;
}

const headers: GridHeader[] = [
    {
        title: "Floor Name",
        class: "freeze-column",
        isSorting: false,
        isFreeze: true
    },
    {
        title: "Units",
        class: "freeze-column",
        isSorting: false,
        isFreeze: true
    },
    {
        title: "Action",
        class: "freeze-column",
        isSorting: false,
        isFilter: false,
        isFreeze: true,
    },
];


const sampleData: ITowerInfo[] = [
    {
        id: "1",
        tower_name: "A",
        image: "",
        floor_info: [
            {
                id: "1",
                floor_name: "1",
                units: ["1", "2", "3", "4", "5"],
                image: ""
            },
            {
                id: "2",
                floor_name: "2",
                units: ["1", "2", "3", "4", "5"],
                image: ""
            }
        ]
    }
]

const ManageTower = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showDeleteModal, setDeleteModal] = useState<boolean>(false);
    const [rows, setRows] = useState<ExpandableRow[]>([]);
    const [showAddTower, setShowAddTower] = useState<boolean>(false);
    const [showAddFloor, setShowAddFloor] = useState<boolean>(false);
    const token = useSelector((state: any) => state.OnBoardingReducer);
    const [permissions, setPermissions] = useState<permissionsInterface | null>(null);
    const [towerData, setTowerData] = useState<ITowerInfo[]>([]);
    const [towerObj, setTowerObj] = useState<ITowerInfo | null>(null);
    const [floorObj, setFloorObj] = useState<IFloorInfo | null>(null);
    const [deleteType, setDeleteType] = useState<"TOWER" | "FLOOR" | null>(null);
    const [showMarkBlade, setShowMarkBlade] = useState<boolean>(false);
    const [baseUrl, setBaseUrl] = useState<string>("");
    const [markType, setMarkType] = useState<"TOWER" | "FLOOR" | "UNIT" | null>(null);
    const [coordinatesData, setCoordinatesData] = useState<ICoordinates[]>([]);
    const [projectImage, setProjectImage] = useState<string>("");

    useEffect(() => {
        if (token && token.token && token.permissions && !!token.permissions.length) {
            const permissions = token.permissions.find((item: any) => item.name === "PROJECTS");
            permissions && permissions?.can_read && getProjectMapData();
            setPermissions(permissions);
        }
    }, [token]);

    const floorActionList = (data: IFloorInfo, tower_data: ITowerInfo) => {
        return (
            <div className="action-btns p-1">
                <button className="cursor-pointer  btn-edit btn border me-1 bg-white" onClick={() => { setFloorObj(data); setTowerObj(tower_data); setShowAddFloor(true); }}>
                    {" "} <Pencil />{" "}
                </button>
                <button onClick={() => { setDeleteType("FLOOR"); setFloorObj(data); setTowerObj(tower_data); setDeleteModal(true); }} className="cursor-pointer btn btn-delete border me-1 bg-white">
                    {" "} <Trash />{" "}
                </button>
                <button onClick={() => { setMarkType("UNIT"); setTowerObj(tower_data); setFloorObj(data); setShowMarkBlade(true) }} className="cursor-pointer btn btn-delete border bg-white" >
                    {" "}
                    <FaMarker />{" Mark Units "}
                </button>
            </div>
        );
    };

    const onEdit = (val: ITowerInfo) => {
        setTowerObj(val);
        setShowAddTower(true);
    };

    const onConfirmDeleteTower = (val: any) => {
        setDeleteType("TOWER");
        setTowerObj(val);
        setDeleteModal(true);
    };

    const getProjectMapData = () => {
        setIsLoading(true);
        WebService.getAPI({ action: `project/detail/${id}`, body: null })
            .then((res: any) => {
                setIsLoading(false);
                if (res.result && res.result?.project_images && res.result?.project_images.length) {
                    setProjectImage(res.result?.project_images[0]?.photo ? `${res.result?.project_images[0]?.photo}` : "");
                    setBaseUrl(res?.base_url || "");
                    let data = res.result?.project_data ? JSON.parse(res.result?.project_data) : [];
                    setTowerData(data?.data ? data.data : []);
                    setCoordinatesData(data?.coordinates_data ? data.coordinates_data : []);
                    drawTable(data?.data ? data.data : []);
                } else {
                    navigate(routePaths.PROJECTPAGE.path);
                }
            })
            .catch((e) => {
                setIsLoading(false);
            });
    };

    const drawTable = (res: ITowerInfo[]) => {
        let temp: any = [];
        for (let i in res) {
            let index: number = 0;
            temp.push({ header: getHeader(res[i]), isExpand: false, data: [], key: res[i], class: "d-flex" });
            index = temp.length - 1;
            let data = [];

            for (let j in res[i].floor_info) {
                data.push({
                    data: [
                        { value: res[i].floor_info[j].floor_name ? res[i].floor_info[j].floor_name : "-" },
                        { value: res[i].floor_info[j]?.units ? res[i].floor_info[j]?.units.length : "-" },
                        { value: floorActionList(res[i].floor_info[j], res[i]) }
                    ]
                });
            };
            temp[index].data = data;
        };
        setRows(temp);
    };

    const getHeader = (data: ITowerInfo) => {
        return (
            <div className="d-flex justify-content-between align-items-center w-100">
                <div className="fw-500">{data.tower_name}</div>
                <div className="action-btns">
                    <button className="cursor-pointer  btn-edit btn border me-1 bg-white" onClick={() => onEdit(data)}>
                        {" "}  <Pencil />{" "}
                    </button>
                    <button onClick={() => onConfirmDeleteTower(data)} className="cursor-pointer btn btn-delete border me-1 bg-white" >
                        {" "}
                        <Trash />{" "}
                    </button>
                    <button onClick={() => { setTowerObj(data); setShowAddFloor(true) }} className="cursor-pointer btn btn-delete border me-1 bg-white" >
                        {" "}
                        <Plus />{" Add Floor "}
                    </button>
                    <button onClick={() => { setMarkType("FLOOR"); setTowerObj(data); setShowMarkBlade(true) }} className="cursor-pointer btn btn-delete border bg-white" >
                        {" "}
                        <FaMarker />{" Mark Floors "}
                    </button>
                </div>
            </div>
        );
    };

    const onDelete = () => {
        setDeleteModal(false);
        if (deleteType === "TOWER" && towerObj) {
            setIsLoading(true);
            WebService.postAPI({
                action: `tower/delete`,
                body: { tower_id: towerObj.id, project_uuid: id },
            })
                .then((res: any) => {
                    setIsLoading(false);
                    toast.success(res.message);
                    const data = towerData.filter((item: ITowerInfo) => item.id !== towerObj.id);
                    setTowerData(data);
                    drawTable(data);
                    setTowerObj(null);
                    setDeleteType(null);
                })
                .catch((e) => {
                    setIsLoading(false);
                    setTowerObj(null);
                    setDeleteType(null);
                });
        } else if (deleteType === "FLOOR" && towerObj && floorObj) {
            setIsLoading(true);
            WebService.postAPI({
                action: `floor/delete`,
                body: { tower_id: towerObj.id, project_uuid: id, floor_id: floorObj.id },
            })
                .then((res: any) => {
                    setIsLoading(false);
                    toast.success(res.message);
                    let data: ITowerInfo[] = towerData.map((item: ITowerInfo) => {
                        if (item.id === towerObj.id) {
                            item.floor_info = item.floor_info.filter((floor: IFloorInfo) => floor.id !== floorObj.id);
                        }
                        return item;
                    });
                    setTowerData(data);
                    drawTable(data);
                    setTowerObj(null);
                    setFloorObj(null);
                    setDeleteType(null);
                })
                .catch((e) => {
                    setIsLoading(false);
                    setTowerObj(null);
                    setFloorObj(null);
                    setDeleteType(null);
                });
        }
    };

    const onCloseTower = (e: boolean, data: ITowerInfo[]) => {
        setShowAddTower(false);
        if (e && data) {
            setTowerData(data);
            drawTable(data);
        }
        setTowerObj(null);
        getProjectMapData();
    };

    const onCloseFloor = (e: boolean, data: ITowerInfo[]) => {
        setShowAddFloor(false);
        if (e && data) {
            setTowerData(data);
            drawTable(data);
        }
        setTowerObj(null);
        setFloorObj(null);
        getProjectMapData();
    };

    const onCloseMark = (e: boolean, data: ITowerInfo[]) => {
        setShowMarkBlade(false);
        if (e && data) {
            setTowerData(data);
            drawTable(data);
        }
        setTowerObj(null);
        setFloorObj(null);
        setMarkType(null);
        getProjectMapData();
    };


    return (
        <>
            <MarkEntity isShow={showMarkBlade} isClose={(e: boolean, data: ITowerInfo[]) => onCloseMark(e, data)} tower_obj={towerObj} floor_obj={floorObj} project_uuid={id || ""} towerData={towerData} base_url={baseUrl} mark_type={markType} project_image={projectImage} coordinates_data={coordinatesData} />
            <AddUpdateTower isShow={showAddTower} isClose={(e: boolean, data: ITowerInfo[]) => onCloseTower(e, data)} data={towerObj} towerData={towerData} project_uuid={id || ""} />
            <ManageFloors isShow={showAddFloor} isClose={(e: boolean, data: ITowerInfo[]) => onCloseFloor(e, data)} data={floorObj} tower_obj={towerObj} towerData={towerData} project_uuid={id || ""} />
            <DeleteModal isShow={showDeleteModal} close={() => { setDeleteModal(false); setDeleteType(null); }} onDelete={() => { onDelete(); }} />
            {permissions && permissions.can_read && <div className="app-page page-dashboard">
                <div className="d-flex justify-content-between align-items-center ">
                    <PageTitle title="Manage Towers" backArrow={false} active={false} />
                    {permissions && permissions.can_write &&
                        <div>
                            <Button className="btn btn-brand-1 mb-3 me-2" onClick={() => setShowAddTower(true)}> {" "}<Plus size={20} className="ms-1" /> Add Tower </Button>
                            <Button className="btn btn-brand-1 mb-3" onClick={() => { setMarkType("TOWER"); setShowMarkBlade(true) }}> <FaMarker /> {" "}Mark Tower </Button>
                        </div>
                    }
                </div>
                <div className="table-card">
                    <ExpandableGrid
                        rows={rows}
                        headers={headers}
                        errorMessage={"No towers found please add towers"}
                        ShowLoader={isLoading}
                    />
                </div>
            </div>}
        </>
    );
};

export default ManageTower;
